import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Field, Input } from '@headlessui/react';
import clsx from 'clsx';
import Pagination from '../shared/Pagination';
import usePrechecksPOS from '../../hooks/usePrechecksPOS';
import { useTranslation } from 'react-i18next';
import PrecheckTable, { BasePrecheckItem } from '../shared/PrecheckTable';

interface PrecheckPos extends BasePrecheckItem {
  additionalServices: string[];
}

const PrechecksPOS: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const { precheckPoses, totalCount, totalPages, loading, error } = usePrechecksPOS({
    search: debouncedSearch,
    page: currentPage,
    limit: 20,
  });

  // Debounce search (client-side filtering and pagination)
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Transform data to match BasePrecheckItem interface
  const transformedPrecheckPoses: PrecheckPos[] = precheckPoses.map((pc: any) => ({
    id: pc.id,
    company: pc.company,
    contact: pc.contactPerson,
    email: pc.email,
    sourceDomain: pc.sourceDomain,
    createdAt: pc.createdAt,
    channel: pc.channel,
    additionalServices: pc.additionalServices,
  }));

  const renderMiddleColumns = (pc: PrecheckPos) => (
    <td className="border px-2 py-2 break-words">
      {pc.additionalServices.map((service, index) => (
        <span className="inline-block rounded-lg px-2 py-1 m-1 bg-yellow-100 text-xs" key={index}>
          {service}
        </span>
      ))}
    </td>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('prechecksPOS.title')}</h1>
          <p className="text-slate-600 mt-1">{t('prechecksPOS.subtitle')}</p>
        </div>
        <button
          onClick={() => navigate('/add-precheck-pos')}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl shadow-sm hover:shadow-md transition-all"
        >
          <CheckCircleIcon className="size-5" />
          <span>{t('prechecksPOS.addPrecheckPOS')}</span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <Field>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder={t('prechecksPOS.searchPlaceholder')}
              value={searchTerm}
              onChange={handleSearchChange}
              className={clsx(
                'block w-full rounded-lg border border-slate-300 bg-white py-3 pl-12 pr-4 text-slate-900',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                'placeholder:text-slate-400'
              )}
            />
          </div>
        </Field>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-2 text-slate-600">{t('prechecksPOS.loading')}</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {precheckPoses.length > 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <PrecheckTable
                  data={transformedPrecheckPoses}
                  type="POS"
                  renderMiddleColumns={renderMiddleColumns}
                  middleHeaders={[t('merchant.additionalServices')]}
                />
              </div>
              <div className="border-t border-slate-200 px-6 py-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  onPageChange={handlePageChange}
                  itemsPerPage={20}
                />
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
              <CheckCircleIcon className="mx-auto h-12 w-12 text-slate-400" />
              <h3 className="mt-4 text-lg font-semibold text-slate-900">
                {searchTerm ? t('prechecksPOS.noResults') : t('prechecksPOS.noPrechecksPOSFound')}
              </h3>
              {!searchTerm && (
                <p className="mt-2 text-slate-600">No POS precheck submissions yet</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PrechecksPOS;
